<!-- src/views/UpdatePayRatesView.vue -->
<template>
  <div class="p-6 max-w-7xl mx-auto">
    <h3 class="text-center">UPDATE OFFICER PAY RATES</h3>

    <!-- Controls -->
    <div class="flex items-center justify-center gap-4 mb-6 flex-wrap">
      <div class="flex items-center gap-2">
        <label class="font-medium">Start Date</label>
        <input type="date" v-model="startDate" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">End Date</label>
        <input type="date" v-model="endDate" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">Search Officer</label>
        <input type="text" v-model="searchString" placeholder="First or last name" class="border rounded px-3 py-1">
      </div>
      <div class="flex items-center gap-2">
        <label class="font-medium">Group</label>
        <select v-model="selectedGroupId" class="border rounded px-3 py-1">
          <option :value="null">All Officers</option>
          <option v-for="group in officerGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
        </select>
      </div>
      <button @click="displayPayRates" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isLoading ? 'Loading...' : 'Display Pay Rates' }}
      </button>
      <button v-if="results && results.length > 0" @click="updatePayRates" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
        {{ isUpdating ? 'Updating...' : 'Update Pay Rates' }}
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="border-collapse border mx-auto">
        <thead>
          <tr class="bg-warning">
            <th class="border p-2 text-left">Officer</th>
            <th class="border p-2 text-right">Current Pay Rate</th>
            <th class="border p-2 text-right">New Pay Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!results || results.length === 0">
            <td colspan="3" class="border p-2 text-center text-gray-500">No records found</td>
          </tr>
          <tr v-for="(row, index) in results" :key="index">
            <td class="border p-2 text-left">{{ row.officerName || '-' }}</td>
            <td class="border p-2 text-right">{{ row.payRate?.toFixed(2) || '-' }}</td>
            <td class="border p-2 text-right">
              <input type="number" step="0.01" v-model.number="row.newPayRate" class="border rounded px-2 py-1 w-28 text-right">
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const toDateInputValue = (date) => date.toISOString().slice(0, 10)

const today = new Date()
const oneMonthLater = new Date(today)
oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)

const startDate = ref(toDateInputValue(today))
const endDate = ref(toDateInputValue(oneMonthLater))
const searchString = ref('')
const isLoading = ref(false)
const isUpdating = ref(false)
const isBusy = ref(false)
const results = ref([])
const selectedGroupId = ref(null)
const officerGroups = ref([])

onMounted(() => {
  const cachedData = localStorage.getItem("lookupData")
  if (!cachedData) return
  officerGroups.value = JSON.parse(cachedData).officerGroups || []
})

const displayPayRates = async () => {
  if (!startDate.value || !endDate.value) { alert('Please select both dates'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Pays/GetOfficerPayRates", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value, searchString: searchString.value, officerGroupId: selectedGroupId.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.officerPayRates || []
  } catch (e) {
    alert('Failed to load officer pay rates: ' + e.message)
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const updatePayRates = async () => {
  if (!localStorage.token) { alert('Please login first'); return }

  isUpdating.value = true
  isBusy.value = true
  try {
    const updates = results.value.map(r => ({ officerId: r.officerId, newPayRate: r.newPayRate }))
    const response = await fetch(props.baseUrl + "Pays/UpdatePayRates", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ startDate: startDate.value, endDate: endDate.value, searchString: searchString.value, updates, officerGroupId: selectedGroupId.value })
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    results.value = data.officerPayRates || []
    alert(data.message || 'Pay rates updated successfully')
  } catch (e) {
    alert('Failed to update pay rates: ' + e.message)
  } finally {
    isUpdating.value = false
    isBusy.value = false
  }
}
</script>
